import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Science as TestIcon,
  DeleteOutline as DeleteOutlineIcon,
  Key as KeyIcon,
  VpnKey as VpnKeyIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  settingsService,
  Setting,
  Secret,
  SettingsStatus,
} from '@/services/settingsService';

interface EditingSecret {
  key: string;
  value: string;
  category: string;
  description: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  email: 'Email Configuration',
  payment: 'Payment Integration',
  location: 'Location Services',
  oauth: 'Social Login (OAuth)',
  monitoring: 'Monitoring',
  security: 'Security',
  feature_flags: 'Feature Flags',
  push_notifications: 'Push Notifications',
  weather: 'Weather',
};

const CATEGORY_ORDER = [
  'general',
  'email',
  'payment',
  'location',
  'oauth',
  'push_notifications',
  'weather',
  'monitoring',
  'security',
  'feature_flags',
];

// Secret Edit Dialog Component
interface SecretEditDialogProps {
  open: boolean;
  secret: EditingSecret | null;
  showValue: boolean;
  saving: boolean;
  onToggleVisibility: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onClose: () => void;
}

const SecretEditDialog: React.FC<SecretEditDialogProps> = ({
  open,
  secret,
  showValue,
  saving,
  onToggleVisibility,
  onChange,
  onSave,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Edit Secret
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {secret?.description}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {secret?.key}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Secret Value"
          type={showValue ? 'text' : 'password'}
          value={secret?.value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter new value"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={onToggleVisibility}
                  edge="end"
                  size="small"
                >
                  {showValue ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !secret?.value}
          startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<Record<string, Setting[]>>({});
  const [secrets, setSecrets] = useState<Record<string, Secret[]>>({});
  const [status, setStatus] = useState<SettingsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['general', 'feature_flags']);
  const [editingSecret, setEditingSecret] = useState<EditingSecret | null>(null);
  const [showSecretValue, setShowSecretValue] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string | boolean | number>>({});
  const [testingSecret, setTestingSecret] = useState<string | null>(null);
  const [deletingSecretKey, setDeletingSecretKey] = useState<string | null>(null);
  const [generatingVapidKeys, setGeneratingVapidKeys] = useState(false);
  const [resettingCircuitBreakers, setResettingCircuitBreakers] = useState(false);
  const [clearingTileCache, setClearingTileCache] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsData, secretsData, statusData] = await Promise.all([
        settingsService.getSettings(),
        settingsService.getSecrets(),
        settingsService.getStatus(),
      ]);
      setSettings(settingsData.settings);
      setSecrets(secretsData.secrets);
      setStatus(statusData);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key: string, value: string | boolean | number) => {
    setPendingChanges(prev => {
      const next = { ...prev, [key]: value };

      // Turning geocoding OFF: force-disable dependent features
      if (key === 'GEOCODING_ENABLED' && value === false) {
        const allSettings = Object.values(settings).flat();
        const dependents = allSettings.filter(s => s.requires === 'GEOCODING_ENABLED');
        for (const dep of dependents) {
          next[dep.key] = false;
        }
      }

      return next;
    });
  };

  const getSettingValue = (setting: Setting): string | boolean | number => {
    if (pendingChanges[setting.key] !== undefined) {
      return pendingChanges[setting.key];
    }
    if (setting.valueType === 'boolean') {
      return setting.value === 'true';
    }
    if (setting.valueType === 'number') {
      return Number(setting.value);
    }
    return setting.value;
  };

  const handleSaveSettings = async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    try {
      setSaving(true);
      const changes = Object.entries(pendingChanges).map(([key, value]) => ({ key, value }));
      const result = await settingsService.updateSettings(changes);

      const failed = result.results.filter(r => !r.success);
      if (failed.length > 0) {
        toast.error(`Failed to save: ${failed.map(f => f.key).join(', ')}`);
      } else {
        toast.success('Settings saved successfully');
        setPendingChanges({});
        await loadData();
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecret = async () => {
    if (!editingSecret) return;

    try {
      setSaving(true);
      await settingsService.updateSecret(
        editingSecret.key,
        editingSecret.value,
        editingSecret.category
      );
      toast.success('Secret saved successfully');
      setEditingSecret(null);
      setShowSecretValue(false);
      await loadData();
    } catch (error) {
      console.error('Failed to save secret:', error);
      toast.error('Failed to save secret');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSecret = async (key: string) => {
    try {
      setTestingSecret(key);
      const result = await settingsService.testSecret(key);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Failed to test secret:', error);
      toast.error('Failed to test secret');
    } finally {
      setTestingSecret(null);
    }
  };

  const handleDeleteSecret = async () => {
    if (!deletingSecretKey) return;

    try {
      setSaving(true);
      await settingsService.deleteSecret(deletingSecretKey);
      toast.success('Secret cleared successfully');
      setDeletingSecretKey(null);
      await loadData();
    } catch (error) {
      console.error('Failed to clear secret:', error);
      toast.error('Failed to clear secret');
    } finally {
      setSaving(false);
    }
  };

  const handleClearTileCache = async () => {
    try {
      setClearingTileCache(true);
      const result = await settingsService.clearTileCache();
      if (result.success) {
        toast.success(result.message || 'Tile cache cleared successfully');
      } else {
        toast.error('Failed to clear tile cache');
      }
    } catch (error) {
      console.error('Failed to clear tile cache:', error);
      toast.error('Failed to clear tile cache');
    } finally {
      setClearingTileCache(false);
    }
  };

  const handleResetCircuitBreakers = async () => {
    try {
      setResettingCircuitBreakers(true);
      const result = await settingsService.resetCircuitBreakers();
      if (result.success) {
        toast.success(result.message || 'Circuit breakers reset successfully');
      } else {
        toast.error('Failed to reset circuit breakers');
      }
    } catch (error) {
      console.error('Failed to reset circuit breakers:', error);
      toast.error('Failed to reset circuit breakers');
    } finally {
      setResettingCircuitBreakers(false);
    }
  };

  const handleGenerateVapidKeys = async () => {
    try {
      setGeneratingVapidKeys(true);
      const result = await settingsService.generateVapidKeys();
      if (result.success) {
        toast.success('VAPID keys generated successfully');
        await loadData();
      } else {
        toast.error('Failed to generate VAPID keys');
      }
    } catch (error) {
      console.error('Failed to generate VAPID keys:', error);
      toast.error('Failed to generate VAPID keys');
    } finally {
      setGeneratingVapidKeys(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const isSignupsEnabled = (): boolean => {
    if (pendingChanges['PUBLIC_SIGNUPS_ENABLED'] !== undefined) {
      return pendingChanges['PUBLIC_SIGNUPS_ENABLED'] === true || pendingChanges['PUBLIC_SIGNUPS_ENABLED'] === 'true';
    }
    const allSettings = Object.values(settings).flat();
    const signupSetting = allSettings.find(s => s.key === 'PUBLIC_SIGNUPS_ENABLED');
    return signupSetting?.value === 'true';
  };

  const renderSetting = (setting: Setting) => {
    const value = getSettingValue(setting);
    const hasChange = pendingChanges[setting.key] !== undefined;

    if (setting.valueType === 'boolean') {
      const isWaitlistDisabledBySignups = setting.key === 'WAITLIST_ENABLED' && isSignupsEnabled();
      const isDisabledByProvider = !!setting.disabledByProvider;
      // Re-evaluate dependency based on pending changes (e.g. geocoding toggled on but not yet saved)
      let isDisabledByDependency = !!setting.disabledByDependency;
      if (setting.requires && pendingChanges[setting.requires] !== undefined) {
        isDisabledByDependency = !pendingChanges[setting.requires];
      }
      const isDisabled = isWaitlistDisabledBySignups || isDisabledByProvider || isDisabledByDependency;
      return (
        <FormControlLabel
          control={
            <Switch
              checked={isDisabled ? false : value as boolean}
              onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
              color={hasChange ? 'warning' : 'primary'}
              disabled={isDisabled}
            />
          }
          label={
            <Box>
              <Typography variant="body2">{setting.description}</Typography>
              <Typography variant="caption" color={isDisabled ? 'warning.main' : 'text.secondary'}>
                {isWaitlistDisabledBySignups
                  ? 'Disabled when public signups are enabled'
                  : isDisabledByProvider
                  ? (setting.providerConstraintMessage || 'Not available with current provider')
                  : isDisabledByDependency
                  ? (setting.dependencyMessage || 'Requires a dependent setting to be enabled')
                  : (setting.helpText || setting.key)}
              </Typography>
            </Box>
          }
        />
      );
    }

    if (setting.options && setting.options.length > 0) {
      return (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{setting.description}</InputLabel>
          <Select
            value={value as string}
            label={setting.description}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            sx={hasChange ? { borderColor: 'warning.main' } : {}}
          >
            {setting.options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {setting.key}
          </Typography>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        label={setting.description}
        value={value}
        onChange={(e) => handleSettingChange(setting.key, setting.valueType === 'number' ? Number(e.target.value) : e.target.value)}
        type={setting.valueType === 'number' ? 'number' : 'text'}
        helperText={setting.key}
        sx={{ mb: 2 }}
        color={hasChange ? 'warning' : 'primary'}
      />
    );
  };

  const renderSecret = (secret: Secret) => {
    const isEditing = editingSecret?.key === secret.key;
    const isTesting = testingSecret === secret.key;

    return (
      <Box
        key={secret.key}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">{secret.description}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {secret.key}
            </Typography>
            <Chip
              size="small"
              label={secret.hasValue ? 'Configured' : 'Not Set'}
              color={secret.hasValue ? 'success' : 'default'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {secret.source === 'environment' && (
              <Chip
                size="small"
                label="From ENV"
                color="info"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {secret.testable && secret.hasValue && (
            <Button
              size="small"
              variant="outlined"
              startIcon={isTesting ? <CircularProgress size={16} /> : <TestIcon />}
              onClick={() => handleTestSecret(secret.key)}
              disabled={isTesting || isEditing}
            >
              Test
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() => {
              if (isEditing) {
                setEditingSecret(null);
                setShowSecretValue(false);
              } else {
                setEditingSecret({
                  key: secret.key,
                  value: '',
                  category: secret.category,
                  description: secret.description,
                });
              }
            }}
            color={isEditing ? 'error' : 'default'}
          >
            {isEditing ? <CloseIcon /> : <EditIcon />}
          </IconButton>
          {secret.hasValue && secret.source !== 'environment' && (
            <IconButton
              size="small"
              onClick={() => setDeletingSecretKey(secret.key)}
              color="default"
              title="Clear secret"
            >
              <DeleteOutlineIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={120} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={280} height={20} sx={{ mb: 3 }} />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage system configuration and secrets
      </Typography>

      {!status?.secretsEnabled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Secrets encryption is disabled. Set BOSS_KEY environment variable to enable secure secret storage.
          Secrets will fall back to environment variables.
        </Alert>
      )}

      {status?.email?.warning && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {status.email.warning}
        </Alert>
      )}

      {/* Save button for settings */}
      {hasPendingChanges && (
        <Card sx={{ mb: 3, position: 'sticky', top: 0, zIndex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, '&:last-child': { pb: 2 } }}>
            <Typography color="warning.main">
              You have unsaved changes
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setPendingChanges({})}
                disabled={saving}
                startIcon={<CloseIcon />}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
              >
                Save
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Secret editing dialog */}
      <SecretEditDialog
        open={!!editingSecret}
        secret={editingSecret}
        showValue={showSecretValue}
        saving={saving}
        onToggleVisibility={() => setShowSecretValue(!showSecretValue)}
        onChange={(value) => editingSecret && setEditingSecret({ ...editingSecret, value })}
        onSave={handleSaveSecret}
        onClose={() => {
          setEditingSecret(null);
          setShowSecretValue(false);
        }}
      />

      {/* Secret delete confirmation dialog */}
      <Dialog
        open={!!deletingSecretKey}
        onClose={() => setDeletingSecretKey(null)}
      >
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><VpnKeyIcon />Clear Secret</Box></DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear{' '}
            {(() => {
              for (const category of Object.values(secrets)) {
                const found = category.find(s => s.key === deletingSecretKey);
                if (found) return found.description;
              }
              return deletingSecretKey;
            })()}
            ? You can re-enter it at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDeletingSecretKey(null)} disabled={saving} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSecret}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings sections */}
      {CATEGORY_ORDER.map(category => {
        const categorySettings = settings[category] || [];
        const categorySecrets = secrets[category] || [];

        if (categorySettings.length === 0 && categorySecrets.length === 0) {
          return null;
        }

        return (
          <Accordion
            key={category}
            expanded={expandedSections.includes(category)}
            onChange={() => toggleSection(category)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 500 }}>
                {CATEGORY_LABELS[category] || category}
              </Typography>
              <Chip
                size="small"
                label={categorySettings.length + categorySecrets.length}
                sx={{ ml: 2, height: 20 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              {/* Settings */}
              {categorySettings.length > 0 && (
                <Box sx={{ mb: categorySecrets.length > 0 ? 3 : 0 }}>
                  {category === 'feature_flags' ? (
                    // Feature flags as a grid of switches
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                      {categorySettings.map(setting => (
                        <Box key={setting.key}>{renderSetting(setting)}</Box>
                      ))}
                    </Box>
                  ) : (
                    categorySettings.map(setting => (
                      <Box key={setting.key}>{renderSetting(setting)}</Box>
                    ))
                  )}
                </Box>
              )}

              {/* Secrets */}
              {categorySecrets.length > 0 && (
                <Box>
                  {categorySettings.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Secrets
                      </Typography>
                    </>
                  )}
                  {categorySecrets.map(secret => renderSecret(secret))}
                </Box>
              )}

              {/* Reset Circuit Breakers for Location Services */}
              {category === 'location' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Re-enable Google APIs if they were automatically disabled due to errors (e.g., after re-enabling billing).
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      startIcon={resettingCircuitBreakers ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={handleResetCircuitBreakers}
                      disabled={resettingCircuitBreakers}
                    >
                      Reset API Circuit Breakers
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={clearingTileCache ? <CircularProgress size={16} /> : <ClearIcon />}
                      onClick={handleClearTileCache}
                      disabled={clearingTileCache}
                    >
                      Clear Map Tile Cache
                    </Button>
                  </Box>
                </Box>
              )}

              {/* VAPID Key Generation for Push Notifications */}
              {category === 'push_notifications' && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="info" sx={{ mb: 2 }}>
                    VAPID keys are required for Web Push notifications. Generate a new key pair below, or manually enter existing keys in the settings above.
                  </Alert>
                  <Button
                    variant="outlined"
                    startIcon={generatingVapidKeys ? <CircularProgress size={16} /> : <KeyIcon />}
                    onClick={handleGenerateVapidKeys}
                    disabled={generatingVapidKeys || !status?.secretsEnabled}
                  >
                    Generate VAPID Keys
                  </Button>
                  {!status?.secretsEnabled && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      Secrets encryption is disabled. BOSS_KEY must be configured to generate keys.
                    </Typography>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default SettingsPanel;
